import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { useSubmissionPolling } from '../hooks/useSubmissionPolling';
import { isContestProblem, getContestSubmissionSettings, validateContestSubmission } from '../utils/contestUtils';
import CodeEditor from '../components/CodeEditor';
import ContestNavbar from '../components/ContestNavbar';
import ContestProblemDescription from '../components/ContestProblemDescription';
import ContestTestResults from '../components/ContestTestResults';
import ContestInputSelectionModal from '../components/ContestInputSelectionModal';
import SubmissionHistory from '../components/SubmissionHistory';
import StdoutDisplay from '../components/StdoutDisplay';
import { PRM_CombinedResponse } from '../../problem_response_models';

// Tab indices for contest problems
const CONTEST_TAB_INDICES = {
  DESCRIPTION: 0,
  RESULTS: 1,
  HISTORY: 2,
} as const;

interface ProblemData {
  readme: string // Base64 encoded
  source: string // Base64 encoded
  compiler_options: string // Base64 encoded
  command_line_arguments: string // Base64 encoded
  default_custom_input?: string // Base64 encoded or null
  problem_set_id?: string
  input_manifest?: any
}

// Helper function to decode base64 strings
const decodeBase64 = (base64String: string): string => {
  try {
    return atob(base64String);
  } catch (error) {
    console.error('Error decoding base64:', error);
    return base64String;
  }
};

export default function ContestProblemEditor() {
  const { problemId } = useParams<{ problemId: string }>();
  const { isAuthenticated, getAccessToken } = useAuth();
  const toast = useToast();
  const [sourceCode, setSourceCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState<PRM_CombinedResponse | null>(null);
  const [submissionToken, setSubmissionToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(CONTEST_TAB_INDICES.DESCRIPTION);
  
  // Track if content has been modified from default
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSourceCode, setOriginalSourceCode] = useState('');
  
  // Contest-specific settings
  const contestSettings = getContestSubmissionSettings();
  const [runParallel, setRunParallel] = useState(contestSettings.runParallel);
  
  // Input selection state
  const [isRunSettingsOpen, setIsRunSettingsOpen] = useState(false);
  const [selectedInputs, setSelectedInputs] = useState<string[]>([]);
  const [enableParallelAnalysis, setEnableParallelAnalysis] = useState(false);
  
  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, md: false });
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Validate that this is actually a contest problem
  if (!problemId || !isContestProblem(problemId)) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          This is not a valid contest problem.
        </Alert>
      </Container>
    );
  }

  const { data: problem, isLoading, error } = useQuery<ProblemData>(
    ['problem', problemId],
    async () => {
      const token = await getAccessToken();
      const response = await api.get(`/problems/${problemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      enabled: isAuthenticated && !!problemId,
      onSuccess: (data) => {
        // Decode the source code from base64
        const decodedSource = decodeBase64(data.source);
        setOriginalSourceCode(decodedSource);
        
        // Check if there's saved content in localStorage first
        const storageKey = `${problemId}-contest-editor-content`;
        const savedContent = localStorage.getItem(storageKey);
        
        if (savedContent) {
          // Use saved content if available
          setSourceCode(savedContent);
          setHasUnsavedChanges(savedContent !== decodedSource);
        } else {
          // Use default content from problem data
          setSourceCode(decodedSource);
          setHasUnsavedChanges(false);
        }
      },
    }
  );

  const { isPolling } = useSubmissionPolling({
    token: submissionToken,
    onComplete: (response: PRM_CombinedResponse) => {
      setSubmissionResponse(response);
      setSubmissionToken(null);
      setIsSubmitting(false);
      // Auto-switch to results tab when submission completes
      setActiveTab(CONTEST_TAB_INDICES.RESULTS);
    },
    onError: (error: string) => {
      console.error('Submission polling error:', error);
      setSubmissionToken(null);
      setIsSubmitting(false);
      toast({
        title: 'Submission Error',
        description: 'Failed to get submission results. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });

  const handleRunSettingsChange = (settings: {
    selectedInputs: string[];
    enableParallelAnalysis: boolean;
  }) => {
    setSelectedInputs(settings.selectedInputs);
    setEnableParallelAnalysis(settings.enableParallelAnalysis);
    setRunParallel(settings.enableParallelAnalysis);
  };

  // Handle source code changes with persistence
  const handleSourceCodeChange = useCallback((val: string) => {
    setSourceCode(val);
    // Save to localStorage for persistence across page reloads
    const storageKey = `${problemId}-contest-editor-content`;
    localStorage.setItem(storageKey, val);
    
    // Check if content is different from original
    setHasUnsavedChanges(val !== originalSourceCode);
  }, [problemId, originalSourceCode]);

  // Handle restoring to default content
  const handleRestoreToDefault = useCallback(() => {
    if (!originalSourceCode) return;
    
    // Update the editor and state
    setSourceCode(originalSourceCode);
    
    // Clear the saved content from localStorage
    const storageKey = `${problemId}-contest-editor-content`;
    localStorage.removeItem(storageKey);
    
    // Reset unsaved changes flag
    setHasUnsavedChanges(false);
    
    toast({
      title: 'Restored to Default',
      description: 'Editor content has been restored to the original problem template.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [originalSourceCode, problemId, toast]);

  const handleSubmit = async () => {
    if (!sourceCode.trim()) {
      toast({
        title: 'Empty Code',
        description: 'Please write some code before submitting.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Contest-specific validation
    const validation = validateContestSubmission(sourceCode);
    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    setSubmissionResponse(null);

    try {
      const token = await getAccessToken();
      
      // Encode the source code to base64
      const encodeBase64 = (str: string): string => {
        return btoa(unescape(encodeURIComponent(str)));
      };

      const response = await api.post(
        `/submissions`,
        {
          problem_id: problemId,
          source_code: encodeBase64(sourceCode),
          language_id: contestSettings.languageId,
          compiler_options: encodeBase64(contestSettings.defaultCompilerOptions),
          command_line_arguments: encodeBase64(contestSettings.defaultCommandLineArguments),
          stdin: encodeBase64(''),
          redirect_stderr_to_stdout: contestSettings.redirectStderrToStdout,
          run_parallel: runParallel,
          run_custom: contestSettings.runCustom,
          input_targets: selectedInputs.length > 0 ? selectedInputs : null // Use selected inputs or all if none selected
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { token: submission_token } = response.data;
      setSubmissionToken(submission_token);
      
      toast({
        title: 'Contest Submission Successful',
        description: `Your solution has been submitted and is being evaluated on ${selectedInputs.length > 0 ? selectedInputs.length : 'all'} dataset(s).`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Submission error:', error);
      setIsSubmitting(false);
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit solution. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="info">
          <AlertIcon />
          Please log in to access the contest problem editor.
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxW="container.md" py={8}>
        <Box display="flex" justifyContent="center" py={8}>
          <Spinner size="xl" />
        </Box>
      </Container>
    );
  }

  if (error || !problem) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          Failed to load contest problem. Please try again.
        </Alert>
      </Container>
    );
  }

  // Decode the readme content
  const decodedReadme = decodeBase64(problem.readme);

  return (
    <Box h="100vh" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {/* Contest Navbar */}
      <ContestNavbar
        problemId={problemId}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isPolling={isPolling}
        onOpenRunSettings={() => setIsRunSettingsOpen(true)}
        onRestoreToDefault={handleRestoreToDefault}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Main Content */}
      <Flex flex={1} overflow="hidden" minH={0}>
        {/* Left Panel - Code Editor */}
        <Box 
          flex={1} 
          display="flex" 
          flexDirection="column" 
          bg="white" 
          minH={0}
          minW={isMobile ? "100%" : "300px"}
        >
          <Box 
            bg="gray.50" 
            borderBottom="1px" 
            borderColor={borderColor} 
            px={4} 
            py={3}
            flexShrink={0}
          >
            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Contest Solution Editor
              </Text>
              <HStack spacing={2}>
                <Text fontSize="xs" color="gray.600">
                  C++ with OpenCilk
                </Text>
              </HStack>
            </HStack>
          </Box>

          <Box flex={1} minH={0}>
            <CodeEditor
              value={sourceCode}
              onChange={handleSourceCodeChange}
              language="cpp"
            />
          </Box>
        </Box>

        {/* Right Panel - Contest Info and Results */}
        <Box 
          w={isMobile ? "100%" : "400px"} 
          borderLeft="1px" 
          borderColor={borderColor}
          bg="gray.50"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            flex={1}
            display="flex"
            flexDirection="column"
          >
            <TabList bg="white" borderBottom="1px" borderColor={borderColor}>
              <Tab fontSize="sm">Description</Tab>
              <Tab fontSize="sm">Results</Tab>
              <Tab fontSize="sm">History</Tab>
            </TabList>

            <TabPanels flex={1} overflow="hidden">
              {/* Description Tab */}
              <TabPanel p={0} h="100%" overflow="auto">
                <ContestProblemDescription 
                  problemId={problemId} 
                  readme={decodedReadme} 
                />
              </TabPanel>

              {/* Results Tab */}
              <TabPanel p={4} h="100%" overflow="auto">
                {submissionResponse ? (
                  <VStack spacing={4} align="stretch">
                    <ContestTestResults response={submissionResponse} />
                    <StdoutDisplay response={submissionResponse} />
                  </VStack>
                ) : (
                  <Box textAlign="center" py={8}>
                    <Text color="gray.500">
                      Submit your solution to see results here.
                    </Text>
                  </Box>
                )}
              </TabPanel>

              {/* History Tab */}
              <TabPanel p={0} h="100%" overflow="auto">
                <SubmissionHistory problemId={problemId} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Flex>

      {/* Input Selection Modal */}
      <ContestInputSelectionModal
        isOpen={isRunSettingsOpen}
        onClose={() => setIsRunSettingsOpen(false)}
        problemId={problemId}
        onSettingsChange={handleRunSettingsChange}
      />
    </Box>
  );
} 